import {useParams} from "react-router-dom";
import {InformationCircleIcon} from "@heroicons/react/20/solid";
import {format, formatISO} from "date-fns";
import {useNewsBySlug} from "@/hooks/useNews";
import PodcastIframe from "@/components/PodcastIframe";
import SidebarBanner from "@/components/SidebarBanner.tsx";
import ContentBanner from "@/components/ContentBanner.tsx";
import {MetaTags} from "@/hooks/MetaTags.tsx";

function absoluteUrl(path?: string) {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://www.kosmos.com.na";
    if (!path) return origin;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function stripAndTruncate(input: string, max = 180) {
    const clean = input.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function renderBodyWithIframes(body: string) {
    const regex = /(https?:\/\/iframe\.iono\.fm\/e\/\d+\?layout=modern)/g;

    const parts = body.split(regex);

    return parts.map((part, i) => {
        if (regex.test(part)) {
            return (
                <div key={`iframe-${i}`} className="podcast-block">
                    <PodcastIframe src={part} singleIframe />
                </div>
            );
        }

        return (
            <div key={`text-${i}`} className="whitespace-pre-wrap">
                {part}
            </div>
        );
    });
}


export default function NewsDetailPage() {
    const {slug} = useParams();
    const {data, isLoading, isError} = useNewsBySlug(slug ?? "");

    if (isLoading) return <p>Loading…</p>;
    if (isError || !data) return <p>Not found</p>;

    const url = absoluteUrl(`/nuus/${slug}`);
    const title = data.title;
    const desc = stripAndTruncate(data.summary ?? data.body ?? "", 160);
    const image = data.media?.url;
    const published = data.publishedAt ? formatISO(new Date(data.publishedAt)) : undefined;
    const modified = data.updatedAt ? formatISO(new Date(data.updatedAt)) : published;

    return (
        <>
            <MetaTags
                title={title}
                description={desc}
                image={image}
                url={url}
                published={published}
                modified={modified}
            />

            {/* Page content */}
            <section className="py-16 sm:py-18">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                        <article className="lg:col-span-9">
                            <p className="text-base/7 font-semibold text-blue-900">
                                {data.category?.name}
                            </p>

                            <h1 className="mt-4 text-4xl font-semibold tracking-tight text-pretty text-blue-900 sm:text-5xl">
                                {data.title}
                            </h1>

                            <div className="mt-4 text-md/8">
                                {data.publishedAt && (
                                    <time dateTime={data.publishedAt} className="text-gray-500">
                                        {format(new Date(data.publishedAt), "MMMM d, yyyy HH:mm")}
                                    </time>
                                )}
                            </div>

                            {data.tags && data.tags.length > 0 && (
                                <div className="flex items-center gap-x-4 text-xs">
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {data.tags.map((tag) => (
                                            <span
                                                key={tag.name}
                                                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600"
                                            >
                        {tag.name}
                      </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.summary && (
                                <p className="mt-4 max-w-4xl text-xl/8">{data.summary}</p>
                            )}

                            <ContentBanner placement="ARTICLE_TOP"/>

                            {data.media?.url && (
                                <figure className="mt-10 max-w-4xl">
                                    <img
                                        alt={data.media.caption ?? data.title}
                                        src={data.media.url}
                                        className="aspect-video rounded-xl bg-gray-50 object-cover"
                                    />
                                    {data.media.caption && (
                                        <figcaption className="mt-4 flex gap-x-2 text-sm/6 text-gray-500">
                                            <InformationCircleIcon
                                                aria-hidden="true"
                                                className="mt-0.5 size-5 flex-none text-gray-300"
                                            />
                                            {data.media.caption}
                                        </figcaption>
                                    )}
                                </figure>
                            )}

                            {typeof data.body === "string" && data.body.trim().length > 0 && (() => {
                                const body = data.body;
                                if (body.length <= 500) {
                                    return (
                                        <>
                                            <ContentBanner placement="ARTICLE_INLINE"/>
                                            <div className="prose prose-indigo mt-10 max-w-4xl text-gray-800">
                                                {renderBodyWithIframes(body)}
                                            </div>
                                        </>
                                    );
                                }

                                const mid = Math.floor(body.length / 2);
                                const nextPara = body.indexOf("\n\n", mid);
                                const prevPara = body.lastIndexOf("\n\n", mid);
                                const insertAt = nextPara !== -1 ? nextPara + 2 : prevPara !== -1 ? prevPara + 2 : mid;

                                const before = body.slice(0, insertAt);
                                const after = body.slice(insertAt);

                                return (
                                    <div className="prose prose-indigo mt-10 max-w-4xl text-gray-800">
                                        {renderBodyWithIframes(before)}
                                        <ContentBanner placement="ARTICLE_INLINE"/>
                                        {renderBodyWithIframes(after)}
                                    </div>
                                );
                            })()}

                            {data.ionoLink && (
                                <div className="mt-10 max-w-4xl">
                                    <PodcastIframe src={data.ionoLink} singleIframe/>
                                </div>
                            )}

                            <ContentBanner placement="ARTICLE_BOTTOM"/>
                        </article>

                        <div className="lg:col-span-3">
                            <div className="lg:sticky lg:top-24 space-y-6">
                                <SidebarBanner placement="SIDEBAR_TOP"/>
                                <SidebarBanner placement="SIDEBAR_MIDDLE"/>
                                <SidebarBanner placement="SIDEBAR_BOTTOM"/>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
}
